import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MapPin, Phone, CheckCircle, XCircle, PackageX } from 'lucide-react'
import { publicApi } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import toast from 'react-hot-toast'

// Status da entrega, mesmo padrão de cor/label do StatusBadge de RouteView.jsx
const deliveryStatusConfig = {
  aguardando_saida: { label: 'Aguardando', color: 'bg-gray-100 text-gray-700' },
  em_rota: { label: 'Em rota', color: 'bg-blue-100 text-blue-700' },
  entregue: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  nao_entregue: { label: 'Não entregue', color: 'bg-red-100 text-red-700' },
}

// Página pública (sem login) que o motorista abre a partir do link enviado
// pelo vendedor via WhatsApp. Sem MainLayout/Sidebar de propósito - é uma tela
// isolada, pensada pra abrir direto no navegador do celular.
export default function DriverLinkView() {
  const { token } = useParams()
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = useState(null)

  const { data: route, isLoading, isError } = useQuery({
    queryKey: ['driverLink', token],
    queryFn: async () => {
      const response = await publicApi.get(`/api/driver-link/${token}`)
      return response.data.data.route
    },
    retry: false,
  })

  const confirmDelivery = useMutation({
    mutationFn: (deliveryId) => publicApi.post(`/api/driver-link/${token}/deliveries/${deliveryId}/confirm`),
    onSuccess: () => {
      toast.success('Entrega confirmada!')
      queryClient.invalidateQueries(['driverLink', token])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao confirmar entrega')
    },
  })

  const failDelivery = useMutation({
    mutationFn: ({ deliveryId, reason }) => publicApi.post(`/api/driver-link/${token}/deliveries/${deliveryId}/fail`, {
      failureReason: reason
    }),
    onSuccess: () => {
      toast.success('Entrega marcada como não entregue')
      queryClient.invalidateQueries(['driverLink', token])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar entrega')
    },
  })

  const finishRoute = useMutation({
    mutationFn: () => publicApi.post(`/api/driver-link/${token}/finish`),
    onSuccess: () => {
      toast.success('Rota finalizada!')
      queryClient.invalidateQueries(['driverLink', token])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar rota')
    },
  })

  const handleConfirmDelivery = (deliveryId) => {
    setDialogState({ type: 'confirm', deliveryId })
  }

  const handleFailDelivery = (deliveryId) => {
    setDialogState({ type: 'fail', deliveryId })
  }

  const handleDialogConfirm = (value) => {
    const { type, deliveryId } = dialogState
    setDialogState(null)
    if (type === 'confirm') {
      confirmDelivery.mutate(deliveryId)
    } else {
      failDelivery.mutate({ deliveryId, reason: value })
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // O backend não distingue token inválido, expirado ou rota já finalizada -
  // os três casos voltam como 404, de propósito. Uma mensagem genérica aqui é
  // a coisa certa a fazer, não um bug de UI escondendo detalhe.
  if (isError || !route) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <PackageX className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Este link não está mais disponível</p>
          <p className="text-sm text-gray-500 mt-1">Fale com quem te enviou o link pra conseguir um novo.</p>
        </div>
      </div>
    )
  }

  const deliveries = [...route.deliveries].sort((a, b) => a.sequenceOrder - b.sequenceOrder)

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <h1 className="text-lg font-semibold text-gray-900">Rota de Entregas</h1>
        {route.origin?.address && (
          <p className="text-sm text-gray-500 mt-0.5">Saída: {route.origin.address}</p>
        )}
      </div>

      <div className="p-4 space-y-3">
        {deliveries.map((d) => {
          const statusInfo = deliveryStatusConfig[d.status] || deliveryStatusConfig.aguardando_saida
          return (
            <Card key={d.id} padding="sm">
              <div className="flex justify-between items-start gap-2 mb-2">
                <p className="font-medium text-gray-900">
                  {d.sequenceOrder}. {d.recipientName}
                </p>
                <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>

              <p className="text-sm text-gray-500 flex items-start gap-1">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {d.address.street}, {d.address.number}
                  {d.address.complement ? ` - ${d.address.complement}` : ''} - {d.address.neighborhood}, {d.address.city}/{d.address.state}
                </span>
              </p>

              {d.recipientPhone && (
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                  <Phone className="w-3.5 h-3.5" />
                  {d.recipientPhone}
                </p>
              )}

              {d.status === 'em_rota' && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    variant="secondary"
                    className="text-sm"
                    onClick={() => handleConfirmDelivery(d.id)}
                    isLoading={confirmDelivery.isPending}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Confirmar entrega
                  </Button>
                  <Button
                    variant="secondary"
                    className="text-sm"
                    onClick={() => handleFailDelivery(d.id)}
                    isLoading={failDelivery.isPending}
                  >
                    <XCircle className="w-4 h-4" />
                    Não entregue
                  </Button>
                </div>
              )}
            </Card>
          )
        })}
      </div>

      <div className="px-4">
        {route.status !== 'finalizada' ? (
          <Button
            fullWidth
            onClick={() => finishRoute.mutate()}
            isLoading={finishRoute.isPending}
          >
            Finalizar Rota
          </Button>
        ) : (
          <div className="text-center py-4">
            <p className="text-green-600 font-medium">✓ Rota concluída, obrigado!</p>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!dialogState}
        title={dialogState?.type === 'fail' ? 'Não entregue' : 'Confirmar entrega'}
        message={dialogState?.type === 'fail' ? 'Motivo da não entrega:' : 'Confirmar esta entrega?'}
        showInput={dialogState?.type === 'fail'}
        inputDefaultValue="Cliente ausente"
        onConfirm={handleDialogConfirm}
        onCancel={() => setDialogState(null)}
      />
    </div>
  )
}
